<template>
    <modal
        id="filter-create"
        size="lg"
        :title="getTitle()"
        :okTitle="$t('modal.save')"
        :ok-disabled="isSubmitting"
        :ok-variant="'primary'"
        :cancelTitle="$t('modal.cancel')"
        :cancelDisabled="isSubmitting"
        :disabled="false"
        :noCloseOnEsc="true"
        :noCloseOnBackdrop="true"
        isModalRoute
        @okHandler="onSubmit"
    >
        <loader :loader="loading" />
        <b-container fluid v-show="!loading">
            <b-form>
                <b-row>
                    <b-col sm="12" md="6" xl="6" lg="6">
                        <c-title
                            :state="checkState($v.form.title)"
                            :value.sync="$v.form.title.$model"
                            :disabled="false"
                            :label="$t('targets.title') + '*'"
                            :placeholder="$t('targets.title')"
                        >
                            <span v-if="!$v.form.title.required">
                                {{ $t('form.validations.required') }}
                            </span>
                        </c-title>
                    </b-col>
                    <b-col sm="12" md="6" xl="6" lg="6">
                        <b-form-group :label="$t('dataTable.columns') + '*'">
                            <b-dropdown
                                :text="$t('dataTable.columns')"
                                class="w-100 dropdown-create-modal"
                                :variant="columnsVariant"
                            >
                                <template slot="button-content">
                                    <i class="fas fa-eye"></i>
                                    {{ $t('people.columns') }}
                                </template>
                                <b-dropdown-form>
                                    <div
                                        v-for="(colGroup, index) in defaultFilters.colGroups"
                                        :key="'colgroup-' + index"
                                        class="mb-2"
                                    >
                                        <strong>{{ colGroup.colGroup }}</strong>
                                        <b-form-checkbox-group
                                            :id="`column-` + index"
                                            v-model="$v.form.columns.$model"
                                            :options="getCheckboxOptions(colGroup.columns)"
                                            :name="`col-` + index"
                                            class="d-flex flex-column"
                                        />
                                    </div>
                                </b-dropdown-form>
                            </b-dropdown>
                            <div class="valid-feedback d-block">
                                <span class="invalid" v-if="!$v.form.columns.required">
                                    {{ $t('form.validations.required') }}
                                </span>
                            </div>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col sm="6" md="6" xl="6" lg="6">
                        <b-form-group :label="$t('administrator.periods')">
                            <periods
                                :state="null"
                                :value.sync="periodsSelected"
                                :notGetActive="isEditing()"
                                applyScopeNotArchived
                            />
                        </b-form-group>
                    </b-col>

                    <b-col sm="3" md="3" xl="3" lg="3" v-if="canSetPrivateFilter">
                        <b-form-group class="d-inline-flex mb-2" :label="$t('people.filters.header.private')">
                            <b-form-checkbox switch v-model="privateFilter" :disabled="isRegularUser">
                            </b-form-checkbox>
                        </b-form-group>
                    </b-col>

                    <b-col sm="3" md="3" xl="3" lg="3">
                        <b-form-group :label="$t('people.filters.create.perPage')">
                            <b-form-select v-model="selectedPerPage" :options="perPageOptions" />
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col sm="12" md="12" xl="12" lg="12">
                        <stages
                            :label="$t('people.filterByStage')"
                            :placeholder="$t('people.filterByStage')"
                            :value.sync="filterStages"
                            :loadDataWhenMounted="true"
                        />
                        <managers
                            :state="null"
                            :value.sync="filterManagers"
                            :disabled="false"
                            @loaded="managersReady = $event"
                            :showLabel="true"
                            :loadDataWhenMounted="true"
                            @options-loaded="$store.dispatch('filterManagers', $event)"
                        />
                        <sections
                            :label="$t('form.label.departmentAndUnit')"
                            :value.sync="filterSections"
                            :loadDataWhenMounted="true"
                            @loaded="departmentsReady = $event"
                        />
                        <occupation-groups
                            :state="null"
                            :value.sync="filterOccupationGroups"
                            :disabled="false"
                            @loaded="occupationGroupReady = $event"
                            :showLabel="true"
                            :loadDataWhenMounted="true"
                            @options-loaded="$store.dispatch('filterOccupationGroups', $event)"
                        />
                        <status setDefaultStatus :selected.sync="filterStatus" @updated="filterStatus = $event" />
                        <b-form-group :label="$t('people.isUserReadyToNextStage')">
                            <div class="d-flex align-items-center">
                                <b-form-checkbox v-model="filterOkUser" inline switch>
                                    {{ $t('people.user') }}
                                </b-form-checkbox>
                                <b-form-checkbox v-model="filterOkManager" inline switch>
                                    {{ $t('people.manager') }}
                                </b-form-checkbox>
                            </div>
                        </b-form-group>
                        <b-form-group
                            :label="$t('people.searchByName')"
                            :description="$t('people.instructions.bulkSearch')"
                        >
                            <c-text-area id="bulk-search" :value.sync="filterSearch" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-form>
        </b-container>
        <template slot="okButton">
            <i v-if="isSubmitting" class="fa fa-spinner fa-spin px-2" />
        </template>
    </modal>
</template>

<script>
import i18n from '@/i18n'
import Modal from '@/views/components/modal'
import Loader from '@/views/components/loader'
import { Periods } from '@/views/components/fields'
import {
    CTextArea,
    Departments,
    OccupationGroups,
    Managers,
    SectionsWithoutLevel as Sections,
    Title as CTitle,
    Stages,
    Status,
} from '@/views/components/fields'
import { mapperToOptions } from '@/views/components/selects'

import { getStagesFromApi, createFilter, getFilterFromApi, editFilter } from '../../../api'

import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import arrayFilters from './arrayFilters'
import booleanFilters from './booleanFilters'
import notification, { success, danger } from '@/lib/notification'
import { cloneDeep } from 'lodash'

notification.init()

export default {
    name: 'CreateModal',
    components: {
        Stages,
        Modal,
        Periods,
        CTextArea,
        Departments,
        OccupationGroups,
        Managers,
        Loader,
        Status,
        Sections,
        CTitle,
    },
    mixins: [validationMixin],
    validations: {
        form: {
            title: {
                required,
                maxLength: maxLength(255),
            },
            columns: {
                required,
            },
        },
    },
    data() {
        return {
            arrayFilters,
            booleanFilters,
            periodsSelected: [],
            privateFilter: false,
            perPageOptions: ['5', '10', '20', '30', '50', '100', '300', '500'],
            selectedPerPage: 10,
            form: {
                title: '',
                columns: [],
            },
            stages: [],
            filterSections: [],
            filterStages: [],
            filterManagers: [],
            filterOccupationGroups: [],
            filterStatus: [],
            filterOkUser: false,
            filterOkManager: false,
            filterSearch: '',
            filter: {
                'eventUser.event_id': [],
                'groups.id': [],
                manager_id: [],
                'status.id': [],
                'eventUser.ok_user': 0,
                'eventUser.ok_manager': 0,
            },
            storeFilters: [],
            departmentsReady: false,
            managersReady: false,
            occupationGroupReady: false,
            editing: false,
            id: null,
            isSubmitting: false,
            loading: true,
        }
    },
    mounted() {
        getStagesFromApi().then((resp) => {
            this.stages = resp
            if (this.$route.params.id) {
                this.setupEditing()
            }
        })
        this.form.columns = this.getAllColumns()

        if (this.isRegularUser) {
            this.privateFilter = true
        }
    },
    methods: {
        setupEditing() {
            this.editing = true
            this.id = this.$route.params.id
            getFilterFromApi(this.id).then((filter) => this.parseFilterData(filter))
        },
        parseFilterData({ advanced_search_fields, columns, per_page, periods, title, user_id, sections }) {
            this.form.title = title
            this.selectedPerPage = per_page
            this.periodsSelected = this.mapDefaultFilterPeriods(periods)
            this.privateFilter = !!user_id
            this.form.columns = this.mapColumns(columns)
            this.filterSections = mapperToOptions(sections, 'id', 'name')

            if (advanced_search_fields !== null && advanced_search_fields !== undefined) {
                this.mapAdvancedFields(advanced_search_fields)
            }
        },
        mapColumns(columns) {
            let cols = []
            this.defaultFilters.colGroups.forEach((element) => {
                cols = cols.concat(element.columns.filter((el) => columns.map((item) => item.key).includes(el.key)))
            })
            return cols
        },
        intersectArrays(arr1, arr2, key, includeKey) {
            return arr1.filter((element) => arr2[key].includes(element[includeKey]))
        },
        mapAdvancedFields({ names, filter }) {
            this.filterSearch = this.mapFilterSearch(names)
            const isEmpty = typeof filter === 'object' && Object.keys(filter).length === 0
            if (isEmpty) {
                return
            }

            this.filter = cloneDeep(filter)
            this.storeFilters = this.defaultFilters
            Object.entries(filter).forEach(([key, value]) => this.parseAdvancedField(key, value, cloneDeep(filter)))
        },
        mapFilterSearch(names) {
            if (names.length) {
                return names.join('\n')
            }
            return ''
        },
        mapDefaultFilterPeriods(periods) {
            return periods.map((element) => {
                const keys = Object.keys(element)
                const keysMapped = keys.map((item) => this.parseCamelCase(item))
                const obj = {}
                for (let i = 0; i < keys.length; i++) {
                    obj[keysMapped[i]] = element[keys[i]]
                }
                return obj
            })
        },
        parseAdvancedField(key, value, filter) {
            if (typeof value === 'number') {
                this[this.booleanFilters[key]] = !!value
                return
            }

            const [stateDataName, componentDataName, intersectionFieldName] = this.arrayFilters[key]

            if (stateDataName.includes('store')) {
                const storeProperty = stateDataName.substr(stateDataName.indexOf('.') + 1, stateDataName.length - 1)

                this[componentDataName] = this.intersectArrays(
                    this.storeFilters[storeProperty],
                    filter,
                    key,
                    intersectionFieldName
                )
                return
            }

            this[componentDataName] = mapperToOptions(
                this.intersectArrays(this[stateDataName], filter, key, intersectionFieldName),
                'id',
                'title'
            )
        },
        parseCamelCase(str) {
            const parseNames = {
                name: 'label',
                id: 'value',
            }

            if (Object.keys(parseNames).includes(str.toLowerCase())) {
                str = parseNames[str.toLowerCase()]
            }
            return str.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''))
        },
        onSubmit(event) {
            event.preventDefault()
            if (!this.validate()) {
                return
            }
            this.isSubmitting = true

            const data = {
                columns: this.form.columns,
                periods: this.getPeriodsIds(),
                private: this.privateFilter,
                per_page: this.selectedPerPage,
                title: this.form.title,
                advanced_search_fields: {
                    filter: this.filter,
                    names: this.transformTextIntoNamesArray(),
                    sections: this.filterSections.map((item) => item.value),
                },
                company_id: this.$store.state.currentUser.user.company_id,
            }

            if (data.private) {
                data['user_id'] = this.$store.state.currentUser.user.id
            }
            if (!data.private) {
                data['user_id'] = null
            }

            let promise
            let notificationType
            if (this.editing) {
                data['id'] = this.id
                promise = editFilter(data, this.id)
                notificationType = 'edit'
            } else {
                promise = createFilter(data)
                notificationType = 'create'
            }

            promise
                .then(() => {
                    this.notification(notificationType)
                    this.isSubmitting = false
                    this.$store.dispatch('getFilters')
                    this.$router.back(-1)
                })
                .catch(() => {
                    this.notification('error')
                    this.isSubmitting = false
                })
        },
        notification(type) {
            if (type == 'create') {
                return success(
                    i18n.t('notification.createdSuccessfully', {
                        item: i18n.t('people.filters.title'),
                    })
                )
            }

            if (type == 'edit') {
                return success(
                    i18n.t('notification.editedSuccessfully', {
                        item: i18n.t('people.filters.title'),
                    })
                )
            }

            return danger()
        },
        getAllColumns() {
            let allColumns = []
            const colGroups = this.defaultFilters.colGroups.map((colGroup) => colGroup.columns)
            colGroups.forEach((columns) => {
                allColumns = allColumns.concat(columns.map((column) => column))
            })
            return allColumns
        },
        getCheckboxOptions(columns) {
            return columns.map((column) => {
                return {
                    text: column.label,
                    value: column,
                }
            })
        },
        transformTextIntoNamesArray() {
            if (!this.filterSearch) {
                return []
            }
            const SEPARATOR = '\n'
            const newLineSplit = this.filterSearch.split(SEPARATOR)
            if (newLineSplit) {
                return this.removeEmptyLinesAndTrim(newLineSplit)
            }
            return []
        },
        removeEmptyLinesAndTrim(names) {
            return names.reduce((namesToSearch, name) => {
                const nameTrimmed = name.trim()
                if (nameTrimmed.length) {
                    return [...namesToSearch, nameTrimmed]
                }
                return [...namesToSearch]
            }, [])
        },
        getPeriodsIds() {
            return this.periodsSelected.map((period) => period.value)
        },
        validate() {
            this.$v.form.$touch()
            return !this.$v.form.$invalid
        },
        getTitle() {
            return this.$route.name
        },
        isEditing() {
            return this.$route.path.includes('edit')
        },
        checkState(field) {
            return field.$dirty ? !field.$invalid : null
        },
    },
    watch: {
        filterStages: function () {
            this.filter['eventUser.event_id'] = this.filterStages.map((stage) => stage.value)
        },
        filterManagers: function () {
            this.filter['manager_id'] = this.filterManagers.map((manager) => manager.value)
        },
        filterOkUser: function () {
            this.filter['eventUser.ok_user'] = this.filterOkUser ? 1 : 0
        },
        filterOkManager: function () {
            this.filter['eventUser.ok_manager'] = this.filterOkManager ? 1 : 0
        },
        filterOccupationGroups: function () {
            this.filter['groups.id'] = this.filterOccupationGroups.map((occupationGroup) => occupationGroup.value)
        },
        filterDepartments: function () {
            this.filter['teams.id'] = this.filterDepartments.map((department) => department.value)
        },
        filterStatus: function () {
            this.filter['status.id'] = this.filterStatus.map((status) => status.value)
        },
        occupationGroupReady: function () {
            this.loading = !(this.occupationGroupReady && this.departmentsReady && this.managersReady)
        },
        managersReady: function () {
            this.loading = !(this.occupationGroupReady && this.departmentsReady && this.managersReady)
        },
        departmentsReady: function () {
            this.loading = !(this.occupationGroupReady && this.departmentsReady && this.managersReady)
        },
    },
    computed: {
        titleIsValid() {
            return !this.$v.form.title.$anyError
        },
        canSetPrivateFilter() {
            const role = this.$store.state.currentUser.user.role.name
            return ['god', 'administrator', 'local_administrator', 'user'].includes(role)
        },
        columnsVariant() {
            return !this.$v.form.columns.required ? 'outline-danger' : ''
        },
        defaultFilters() {
            return this.$store.state.defaultFilters
        },
        isRegularUser() {
            return this.$store.state.currentUser.user.role.name === 'user'
        },
    },
}
</script>

<style lang="sass">
.dropdown-create-modal
    .dropdown-menu
        height: 450px
        overflow-y: scroll
.invalid
    color: red
</style>
